import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Plus,
  Search,
  Download,
  Edit,
  Trash2,
  Eye,
  Calendar,
  Upload,
  Paperclip,
  X,
  Building2,
  User,
  DollarSign,
  AlertCircle,
  Package
} from 'lucide-react';
import { serviceReportService } from '../../../services/serviceReportService';
import { visitService } from '../../../services/visitService';
import { inventoryService } from '../../../services/inventoryService';
import { useDateFormat } from '../../../hooks/useDateFormat';

const ServiceReports = () => {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [editingReport, setEditingReport] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [partsSearch, setPartsSearch] = useState('');
  const [selectedParts, setSelectedParts] = useState([]);
  const { formatDate } = useDateFormat();
  const [formData, setFormData] = useState({
    visitId: '',
    reportNumber: '',
    reportDate: '',
    workPerformed: '',
    findings: '',
    recommendations: '',
    laborHours: '',
    laborCost: '',
    partsCost: '',
    totalCost: '',
    assetCondition: 'GOOD',
    nextServiceDate: '',
    technicianSignature: '',
    signedBy: ''
  });

  // Mock data
  const mockReports = [
    {
      id: 1,
      reportNumber: 'SR-2024-001',
      visitId: 1,
      visitNumber: 'VIS-2024-001',
      contractId: 1,
      contractNumber: 'MC-2024-001',
      supplierId: 1,
      supplierName: 'TechMaint Solutions',
      reportDate: '2024-02-15',
      workPerformed: 'Completed quarterly HVAC inspection. Replaced air filters on units 1-4. Cleaned condenser coils and checked refrigerant levels.',
      findings: 'Unit 2 showing signs of wear on fan belt. Recommended replacement within 30 days.',
      recommendations: 'Schedule fan belt replacement for Unit 2. Consider upgrading thermostat to smart model for better efficiency.',
      laborHours: 4.5,
      laborCost: 450,
      partsCost: 180,
      totalCost: 630,
      assetCondition: 'GOOD',
      nextServiceDate: '2024-05-15',
      signedBy: 'Mike Johnson',
      files: [
        { id: 1, fileName: 'inspection_photos.pdf', fileType: 'application/pdf', fileSize: 2456000 },
        { id: 2, fileName: 'unit2_damage.jpg', fileType: 'image/jpeg', fileSize: 845000 }
      ]
    },
    {
      id: 2,
      reportNumber: 'SR-2024-002',
      visitId: 3,
      visitNumber: 'VIS-2024-003',
      contractId: 2,
      contractNumber: 'MC-2024-002',
      supplierId: 2,
      supplierName: 'Global Parts Inc',
      reportDate: '2024-03-02',
      workPerformed: 'Performed complete generator service including oil change, filter replacement, and load test.',
      findings: 'All systems operating within normal parameters. Battery showing 95% capacity.',
      recommendations: 'Continue regular maintenance schedule. Monitor battery health at next visit.',
      laborHours: 3,
      laborCost: 300,
      partsCost: 250,
      totalCost: 550,
      assetCondition: 'GOOD',
      nextServiceDate: '2024-06-02',
      signedBy: 'Sarah Williams',
      files: [
        { id: 3, fileName: 'service_checklist.pdf', fileType: 'application/pdf', fileSize: 156000 }
      ]
    },
    {
      id: 3,
      reportNumber: 'SR-2024-003',
      visitId: 5,
      visitNumber: 'VIS-2024-005',
      contractId: 2,
      contractNumber: 'MC-2024-002',
      supplierId: 2,
      supplierName: 'Global Parts Inc',
      reportDate: '2024-04-20',
      workPerformed: 'Emergency repair - replaced failed starter motor and performed diagnostic tests.',
      findings: 'Starter motor failure due to age. Fuel injector showing early signs of clogging.',
      recommendations: 'Schedule fuel system cleaning. Consider replacing fuel injectors during next major service.',
      laborHours: 6,
      laborCost: 720,
      partsCost: 1200,
      totalCost: 1920,
      assetCondition: 'FAIR',
      nextServiceDate: '2024-05-20',
      signedBy: 'Tom Brown',
      files: []
    }
  ];

  const mockVisits = [
    { id: 1, visitNumber: 'VIS-2024-001', contractTitle: 'Annual HVAC Maintenance' },
    { id: 3, visitNumber: 'VIS-2024-003', contractTitle: 'Generator Maintenance Contract' },
    { id: 5, visitNumber: 'VIS-2024-005', contractTitle: 'Generator Maintenance Contract' }
  ];

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- helper identity changes each render; dependency list is intentional
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [reportsData, visitsData, invData] = await Promise.all([
        serviceReportService.getReports(),
        visitService.getVisits(),
        inventoryService.getInventory()
      ]);
      setReports(reportsData || []);
      setVisits(visitsData || []);
      setInventoryItems(invData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setReports(mockReports);
      setVisits(mockVisits);
    } finally {
      setLoading(false);
    }
  };

  const getConditionLabel = (condition) => {
    switch (condition) {
      case 'GOOD': return t('cmms.sr_condGood');
      case 'FAIR': return t('cmms.sr_condFair');
      case 'POOR': return t('cmms.sr_condPoor');
      case 'CRITICAL': return t('cmms.sr_condCritical');
      default: return condition;
    }
  };

  const getConditionBadge = (condition) => {
    const styles = {
      GOOD: 'bg-green-100 text-green-800',
      FAIR: 'bg-yellow-100 text-yellow-800',
      POOR: 'bg-orange-100 text-orange-800',
      CRITICAL: 'bg-red-100 text-red-800'
    };
    return styles[condition] || styles.GOOD;
  };

  const filteredReports = reports.filter(report => {
    return (
      report.reportNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.supplierName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.workPerformed.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const handleCreate = () => {
    setEditingReport(null);
    setFormData({
      visitId: '',
      reportNumber: '',
      reportDate: new Date().toISOString().split('T')[0],
      workPerformed: '',
      findings: '',
      recommendations: '',
      laborHours: '',
      laborCost: '',
      partsCost: '',
      totalCost: '',
      assetCondition: 'GOOD',
      nextServiceDate: '',
      technicianSignature: '',
      signedBy: ''
    });
    setSelectedParts([]);
    setPartsSearch('');
    setFilesToUpload([]);
    setExistingFiles([]);
    setShowModal(true);
  };

  const handleEdit = (report) => {
    setEditingReport(report);
    setFormData({
      visitId: report.visitId,
      reportNumber: report.reportNumber,
      reportDate: report.reportDate,
      workPerformed: report.workPerformed,
      findings: report.findings || '',
      recommendations: report.recommendations || '',
      laborHours: report.laborHours || '',
      laborCost: report.laborCost || '',
      partsCost: report.partsCost || '',
      totalCost: report.totalCost || '',
      assetCondition: report.assetCondition,
      nextServiceDate: report.nextServiceDate || '',
      technicianSignature: report.technicianSignature || '',
      signedBy: report.signedBy || ''
    });
    setSelectedParts([]);
    setPartsSearch('');
    setFilesToUpload([]);
    setExistingFiles(report.files || []);
    setShowModal(true);
  };

  const addPartToReport = (item) => {
    setSelectedParts(prev => {
      if (prev.find(p => p.inventoryId === item.id)) return prev;
      return [...prev, { inventoryId: item.id, name: item.name, partNumber: item.sku, quantity: 1, unitCost: item.unitCost || 0, available: item.quantityOnHand }];
    });
  };

  const removePartFromReport = (inventoryId) => {
    setSelectedParts(prev => prev.filter(p => p.inventoryId !== inventoryId));
  };

  const updateReportPartQty = (inventoryId, qty) => {
    setSelectedParts(prev => prev.map(p =>
      p.inventoryId === inventoryId ? { ...p, quantity: Math.max(1, Math.min(qty, p.available)) } : p
    ));
  };

  const handleAddFile = (e) => {
    const files = Array.from(e.target.files);
    setFilesToUpload(prev => [...prev, ...files.map(file => ({ file, description: '' }))]);
    e.target.value = '';
  };

  const handleRemoveNewFile = (index) => {
    setFilesToUpload(prev => prev.filter((_, i) => i !== index));
  };

  const handleFileDescriptionChange = (index, description) => {
    setFilesToUpload(prev => prev.map((item, i) =>
      i === index ? { ...item, description } : item
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Build payload including typed partsUsed if any selected
      const computedPartsCost = selectedParts.reduce((s, p) => s + (p.unitCost || 0) * p.quantity, 0);
      const payload = {
        ...formData,
        visitId: formData.visitId ? parseInt(formData.visitId) : undefined,
        laborHours: formData.laborHours ? parseFloat(formData.laborHours) : null,
        laborCost: formData.laborCost ? parseFloat(formData.laborCost) : null,
        partsCost: selectedParts.length > 0 ? computedPartsCost : (formData.partsCost ? parseFloat(formData.partsCost) : null),
        totalCost: formData.totalCost ? parseFloat(formData.totalCost) : null,
        partsUsed: selectedParts.map(p => ({
          inventoryId: p.inventoryId,
          name: p.name,
          partNumber: p.partNumber,
          quantity: p.quantity,
          unitCost: p.unitCost,
          totalCost: (p.unitCost || 0) * p.quantity
        }))
      };

      let reportId;
      if (editingReport) {
        await serviceReportService.updateReport(editingReport.id, payload);
        reportId = editingReport.id;
      } else {
        const newReport = await serviceReportService.createReport(payload);
        reportId = newReport.id;
      }

      // Upload any new files
      if (filesToUpload.length > 0 && reportId) {
        for (const fileItem of filesToUpload) {
          try {
            await serviceReportService.uploadFile(reportId, fileItem.file, fileItem.description);
          } catch (fileError) {
            console.error('Error uploading file:', fileItem.file.name, fileError);
          }
        }
      }

      setShowModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving report:', error);
      alert('Failed to save report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this report?')) {
      try {
        await serviceReportService.deleteReport(id);
        loadData();
      } catch (error) {
        console.error('Error deleting report:', error);
      }
    }
  };

  const handleUpload = (report) => {
    setSelectedReport(report);
    setUploadFile(null);
    setUploadDescription('');
    setShowUploadModal(true);
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !selectedReport) return;

    try {
      await serviceReportService.uploadFile(selectedReport.id, uploadFile, uploadDescription);
      setShowUploadModal(false);
      loadData();
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file. Please try again.');
    }
  };

  const handleDeleteFile = async (reportId, fileId) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      try {
        await serviceReportService.deleteFile(reportId, fileId);
        loadData();
      } catch (error) {
        console.error('Error deleting file:', error);
        alert('Failed to delete file. Please try again.');
      }
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Stats
  const stats = {
    total: reports.length,
    thisMonth: reports.filter(r => {
      const reportDate = new Date(r.reportDate);
      const now = new Date();
      return reportDate.getMonth() === now.getMonth() && reportDate.getFullYear() === now.getFullYear();
    }).length,
    totalCost: reports.reduce((sum, r) => sum + (r.totalCost || 0), 0),
    withIssues: reports.filter(r => r.assetCondition !== 'GOOD').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('cmms.serviceReports')}</h1>
          <p className="text-gray-600 mt-1">{t('cmms.sr_subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50">
            <Download className="w-5 h-5 mr-2" />
            {t('common.export')}
          </button>
          <button
            onClick={handleCreate}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t('cmms.sr_newReport')}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-600">{t('cmms.sr_totalReports')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Calendar className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.thisMonth}</div>
              <div className="text-sm text-gray-600">{t('cmms.sr_thisMonth')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalCost)}</div>
              <div className="text-sm text-gray-600">{t('cmms.sr_totalCost')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <AlertCircle className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.withIssues}</div>
              <div className="text-sm text-gray-600">{t('cmms.sr_withIssues')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder={t('common.search') + ' reports...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Reports List */}
      <div className="space-y-4">
        {loading && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">{t('common.loading')}...</p>
          </div>
        )}

        {!loading && filteredReports.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('common.noResults')}</h3>
            <p className="text-gray-500 mb-4">{t('cmms.sr_emptyState')}</p>
            <button
              onClick={handleCreate}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('cmms.sr_newReport')}
            </button>
          </div>
        )}

        {filteredReports.map((report) => (
          <div key={report.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">{report.reportNumber}</h3>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getConditionBadge(report.assetCondition)}`}>
                      {getConditionLabel(report.assetCondition)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">{t('cmms.sr_visitLabel')} {report.visitNumber}</p>
                  <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <Building2 className="w-4 h-4" />
                      {report.supplierName}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(report.reportDate)}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {report.signedBy}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleUpload(report)}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                  title="Upload file"
                >
                  <Upload className="w-5 h-5" />
                </button>
                <button className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                  <Eye className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleEdit(report)}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                >
                  <Edit className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleDelete(report.id)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Work Performed */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_workPerformed')}</h4>
              <p className="text-sm text-gray-600">{report.workPerformed}</p>
            </div>

            {/* Findings & Recommendations */}
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              {report.findings && (
                <div className="bg-yellow-50 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-yellow-800 mb-1">{t('cmms.sr_findings')}</h4>
                  <p className="text-sm text-yellow-700">{report.findings}</p>
                </div>
              )}
              {report.recommendations && (
                <div className="bg-blue-50 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-blue-800 mb-1">{t('cmms.sr_recommendations')}</h4>
                  <p className="text-sm text-blue-700">{report.recommendations}</p>
                </div>
              )}
            </div>

            {/* Costs */}
            <div className="flex flex-wrap items-center gap-6 mb-4 text-sm">
              <div>
                <span className="text-gray-500">{t('cmms.sr_laborLabel')}</span>
                <span className="ml-1 font-medium text-gray-900">{report.laborHours}h ({formatCurrency(report.laborCost)})</span>
              </div>
              <div>
                <span className="text-gray-500">{t('cmms.sr_partsLabel')}</span>
                <span className="ml-1 font-medium text-gray-900">{formatCurrency(report.partsCost)}</span>
              </div>
              <div>
                <span className="text-gray-500">{t('common.total')}:</span>
                <span className="ml-1 font-semibold text-gray-900">{formatCurrency(report.totalCost)}</span>
              </div>
              {report.nextServiceDate && (
                <div>
                  <span className="text-gray-500">{t('cmms.sr_nextServiceLabel')}</span>
                  <span className="ml-1 font-medium text-gray-900">{formatDate(report.nextServiceDate)}</span>
                </div>
              )}
            </div>

            {/* Files */}
            {report.files && report.files.length > 0 && (
              <div className="border-t border-gray-100 pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  {t('cmms.sr_attachments')} ({report.files.length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {report.files.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg group"
                    >
                      <FileText className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-700">{file.fileName}</span>
                      <span className="text-xs text-gray-400">({formatFileSize(file.fileSize)})</span>
                      <button
                        onClick={() => handleDeleteFile(report.id, file.id)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingReport ? t('common.edit') + ' ' + t('cmms.serviceReports') : t('common.create') + ' ' + t('cmms.serviceReports')}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.vis_visitLabel')} *</label>
                  <select
                    value={formData.visitId}
                    onChange={(e) => setFormData({...formData, visitId: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">{t('cmms.vis_selectVisit')}</option>
                    {visits.map(v => (
                      <option key={v.id} value={v.id}>{v.visitNumber} - {v.contractTitle}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_reportNumber')} *</label>
                  <input
                    type="text"
                    value={formData.reportNumber}
                    onChange={(e) => setFormData({...formData, reportNumber: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_reportDate')} *</label>
                  <input
                    type="date"
                    value={formData.reportDate}
                    onChange={(e) => setFormData({...formData, reportDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_assetCondition')}</label>
                  <select
                    value={formData.assetCondition}
                    onChange={(e) => setFormData({...formData, assetCondition: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="GOOD">{t('cmms.sr_condGood')}</option>
                    <option value="FAIR">{t('cmms.sr_condFair')}</option>
                    <option value="POOR">{t('cmms.sr_condPoor')}</option>
                    <option value="CRITICAL">{t('cmms.sr_condCritical')}</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_workPerformed')} *</label>
                <textarea
                  value={formData.workPerformed}
                  onChange={(e) => setFormData({...formData, workPerformed: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_findings')}</label>
                <textarea
                  value={formData.findings}
                  onChange={(e) => setFormData({...formData, findings: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_recommendations')}</label>
                <textarea
                  value={formData.recommendations}
                  onChange={(e) => setFormData({...formData, recommendations: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_laborHours')}</label>
                  <input
                    type="number"
                    step="0.5"
                    value={formData.laborHours}
                    onChange={(e) => setFormData({...formData, laborHours: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_laborCost')}</label>
                  <input
                    type="number"
                    value={formData.laborCost}
                    onChange={(e) => setFormData({...formData, laborCost: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_partsCost')}</label>
                  <input
                    type="number"
                    value={formData.partsCost}
                    onChange={(e) => setFormData({...formData, partsCost: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_totalCost')}</label>
                  <input
                    type="number"
                    value={formData.totalCost}
                    onChange={(e) => setFormData({...formData, totalCost: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_nextServiceDate')}</label>
                  <input
                    type="date"
                    value={formData.nextServiceDate}
                    onChange={(e) => setFormData({...formData, nextServiceDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_signedBy')}</label>
                  <input
                    type="text"
                    value={formData.signedBy}
                    onChange={(e) => setFormData({...formData, signedBy: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Parts Used from Inventory */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2 mb-3">
                  <Package className="w-4 h-4" />
                  {t('cmms.sr_partsUsed')}
                  <span className="text-xs font-normal text-gray-400">{t('cmms.sr_partsOptionalHint')}</span>
                </h3>
                {/* Search */}
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={partsSearch}
                    onChange={e => setPartsSearch(e.target.value)}
                    placeholder={t('cmms.sr_searchInventory')}
                    className="w-full pl-9 pr-4 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {/* Inventory results */}
                <div className="border border-gray-200 rounded-lg max-h-36 overflow-y-auto mb-3">
                  {inventoryItems
                    .filter(i => {
                      const q = partsSearch.toLowerCase();
                      return !q || i.name?.toLowerCase().includes(q) || i.sku?.toLowerCase().includes(q);
                    })
                    .filter(i => i.quantityOnHand > 0)
                    .map(item => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => addPartToReport(item)}
                        className="w-full flex items-center justify-between px-3 py-2 hover:bg-blue-50 text-left border-b border-gray-100 last:border-0"
                      >
                        <div>
                          <div className="text-sm font-medium text-gray-900">{item.name}</div>
                          <div className="text-xs text-gray-500">{item.sku} · {t('cmms.sr_qtyAvailable')} {item.quantityOnHand}</div>
                        </div>
                        <div className="text-sm font-medium text-gray-700">${(item.unitCost || 0).toFixed(2)}</div>
                      </button>
                    ))}
                  {inventoryItems.filter(i => i.quantityOnHand > 0).length === 0 && (
                    <div className="text-center text-sm text-gray-500 py-3">{t('cmms.wod_noStock')}</div>
                  )}
                </div>
                {/* Selected parts */}
                {selectedParts.length > 0 && (
                  <div className="space-y-2">
                    {selectedParts.map(part => (
                      <div key={part.inventoryId} className="flex items-center gap-2 bg-blue-50 rounded-lg px-3 py-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{part.name}</div>
                          <div className="text-xs text-gray-500">${(part.unitCost || 0).toFixed(2)} {t('cmms.sr_perUnit')}</div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button type="button" onClick={() => updateReportPartQty(part.inventoryId, part.quantity - 1)} className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 text-xs">−</button>
                          <span className="w-6 text-center text-sm">{part.quantity}</span>
                          <button type="button" onClick={() => updateReportPartQty(part.inventoryId, part.quantity + 1)} className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 text-xs">+</button>
                        </div>
                        <div className="text-sm font-semibold w-14 text-right">${((part.unitCost || 0) * part.quantity).toFixed(2)}</div>
                        <button type="button" onClick={() => removePartFromReport(part.inventoryId)} className="text-red-400 hover:text-red-600">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <div className="flex justify-between text-sm font-semibold pt-1 border-t border-blue-100">
                      <span className="text-gray-700">{t('cmms.sr_partsTotal')}</span>
                      <span className="text-blue-700">${selectedParts.reduce((s, p) => s + (p.unitCost || 0) * p.quantity, 0).toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Document Attachments Section */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    {t('cmms.sr_reportAttachments')}
                  </h3>
                  <label className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer text-sm">
                    <Upload className="w-4 h-4 mr-1" />
                    {t('cmms.con_addFile')}
                    <input
                      type="file"
                      onChange={handleAddFile}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                      multiple
                    />
                  </label>
                </div>

                {/* Existing Files (when editing) */}
                {existingFiles.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-2">{t('cmms.sr_existingAttachments')}</p>
                    <div className="space-y-2">
                      {existingFiles.map((file) => (
                        <div key={file.id} className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-green-600" />
                            <span className="text-sm text-gray-700">{file.fileName}</span>
                            <span className="text-xs text-gray-400">({formatFileSize(file.fileSize)})</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeleteFile(editingReport.id, file.id)}
                            className="p-1 text-gray-400 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* New Files to Upload */}
                {filesToUpload.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-2">{t('cmms.sr_filesToUpload')}</p>
                    <div className="space-y-2">
                      {filesToUpload.map((item, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
                          <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-700 truncate">{item.file.name}</p>
                            <input
                              type="text"
                              placeholder={t('cmms.sr_fileDescriptionPh')}
                              value={item.description}
                              onChange={(e) => handleFileDescriptionChange(index, e.target.value)}
                              className="w-full mt-1 px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveNewFile(index)}
                            className="p-1 text-gray-400 hover:text-red-600 flex-shrink-0"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {existingFiles.length === 0 && filesToUpload.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-4">
                    {t('cmms.sr_noAttachments')}
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={submitting}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? t('common.loading') + '...' : (editingReport ? t('common.update') + ' ' + t('cmms.sr_serviceReport') : t('common.create') + ' ' + t('cmms.sr_serviceReport'))}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">{t('cmms.sr_uploadFile')}</h2>
              <button onClick={() => setShowUploadModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleFileUpload} className="p-6 space-y-4">
              <div className="bg-blue-50 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  {t('cmms.con_uploadingTo')} <strong>{selectedReport?.reportNumber}</strong>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.con_fileLabel')} *</label>
                <input
                  type="file"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('cmms.con_supportedFormats')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')}</label>
                <input
                  type="text"
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Inspection photos, Repair documentation"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {t('cmms.con_upload')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceReports;
